import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";
import { ProductImageEditor } from "../components/ProductImageEditor";

const EMPTY = {
    name: "",
    slug: "",
    category: "sarees",
    subType: "",
    description: "",
    fabric: "",
    color: "",
    sizes: [],
    price: 0,
    compareAtPrice: null,
    images: [],
    occasion: [],
    isNew: false,
    isBestSeller: false,
    inStock: true,
    isHidden: false,
};

const SAREE_SUBTYPES = ["Banarasi", "Kanjivaram", "Chiffon", "Cotton", "Silk", "Designer", "Bridal", "Casual", "Partywear"];

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null); // null or product
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("");

    const refresh = () =>
        api.get("/admin/products")
            .then(({ data }) => setProducts(data || []))
            .catch(() => setProducts([]));

    useEffect(() => {
        refresh();
        api.get("/categories").then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
    }, []);

    const startCreate = () => {
        setEditing({ ...EMPTY });
        setOpen(true);
    };
    const startEdit = (p) => {
        setEditing({ ...EMPTY, ...p });
        setOpen(true);
    };
    const closeModal = () => {
        setOpen(false);
        setEditing(null);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!editing) return;
        setBusy(true);
        try {
            const payload = { ...editing };
            ["sizes", "images", "occasion"].forEach((k) => {
                if (typeof payload[k] === "string") {
                    payload[k] = payload[k].split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
                }
            });
            payload.price = Number(payload.price) || 0;
            if (payload.compareAtPrice === "" || payload.compareAtPrice == null) {
                payload.compareAtPrice = null;
            } else {
                payload.compareAtPrice = Number(payload.compareAtPrice);
            }
            if (editing.id) {
                await api.patch(`/admin/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Product created");
            }
            closeModal();
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/admin/products/${id}`);
            toast.success("Product deleted");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to delete");
        }
    };

    const toggleHidden = async (p) => {
        try {
            await api.patch(`/admin/products/${p.id}`, { isHidden: !p.isHidden });
            refresh();
        } catch (e2) {
            toast.error("Failed to toggle visibility");
        }
    };

    const filtered = products.filter((p) => {
        if (!filter) return true;
        const f = filter.toLowerCase();
        return p.name.toLowerCase().includes(f) || p.category.includes(f) || (p.subType || "").toLowerCase().includes(f);
    });

    return (
        <div data-testid="admin-products-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Catalog</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Products</h1>
                    <p className="mt-1 text-inkMuted">{products.length} pieces in your boutique.</p>
                </div>
                <div className="flex items-center gap-3">
                    <input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search products"
                        className="rounded-xl bg-ivory border border-inkDark/10 px-4 py-2.5 text-sm outline-none focus:border-skyblueDeep"
                        data-testid="admin-products-search"
                    />
                    <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-product-button">
                        <Plus size={14} /> Add product
                    </button>
                </div>
            </div>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-products-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Product</th>
                                <th className="text-left px-4 py-3 font-medium">Category</th>
                                <th className="text-left px-4 py-3 font-medium">Price</th>
                                <th className="text-left px-4 py-3 font-medium">Status</th>
                                <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr key={p.id} className="border-t border-inkDark/5">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {p.images?.[0] ? (
                                                <img src={p.images[0]} alt="" className="w-10 h-12 rounded-md object-cover" />
                                            ) : (
                                                <div className="w-10 h-12 rounded-md bg-beige" />
                                            )}
                                            <div>
                                                <div className="text-inkDark font-medium">{p.name}</div>
                                                <div className="text-xs text-inkMuted">{p.subType || p.fabric}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 capitalize">{p.category.replace(/-/g, " ")}</td>
                                    <td className="px-4 py-3">{formatINR(p.price)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {p.isHidden ? (
                                                <span className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700">Hidden</span>
                                            ) : (
                                                <span className="text-[10px] px-2 py-1 rounded-full bg-green-50 text-green-700">Live</span>
                                            )}
                                            {p.isNew && <span className="text-[10px] px-2 py-1 rounded-full bg-skyblue/40 text-inkDark">New</span>}
                                            {p.isBestSeller && <span className="text-[10px] px-2 py-1 rounded-full bg-beige text-inkDark">Bestseller</span>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => toggleHidden(p)} className="p-2 rounded-lg hover:bg-beige" title={p.isHidden ? "Show" : "Hide"} data-testid={`admin-product-toggle-hidden-${p.slug}`}>
                                                {p.isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                                            </button>
                                            <button onClick={() => startEdit(p)} className="p-2 rounded-lg hover:bg-beige" title="Edit" data-testid={`admin-product-edit-${p.slug}`}>
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-product-delete-${p.slug}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-inkMuted">No products yet.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={closeModal} data-testid="admin-product-modal">
                    <form
                        onSubmit={save}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-ivory w-full max-w-3xl rounded-2xl shadow-lift max-h-[90vh] overflow-y-auto"
                    >
                        <div className="p-6 border-b border-inkDark/5 flex items-center justify-between">
                            <div>
                                <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                                <h2 className="font-display text-2xl">{editing.id ? "Edit product" : "Add product"}</h2>
                            </div>
                            <button type="button" onClick={closeModal} className="text-inkMuted">Close</button>
                        </div>
                        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                            <Field label="Name">
                                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-product-name-input" />
                            </Field>
                            <Field label="Slug (optional)">
                                <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" placeholder="auto-generated from name" />
                            </Field>
                            <Field label="Category">
                                <select required value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-product-category-select">
                                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                                </select>
                            </Field>
                            <Field label="Saree Sub-type (only for sarees)">
                                <select value={editing.subType || ""} onChange={(e) => setEditing({ ...editing, subType: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5">
                                    <option value="">—</option>
                                    {SAREE_SUBTYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="Price (INR)">
                                <input required type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-product-price-input" />
                            </Field>
                            <Field label="Compare-at price (INR, optional)">
                                <input type="number" value={editing.compareAtPrice ?? ""} onChange={(e) => setEditing({ ...editing, compareAtPrice: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Fabric">
                                <input value={editing.fabric || ""} onChange={(e) => setEditing({ ...editing, fabric: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Colour">
                                <input value={editing.color || ""} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Sizes (comma-separated)">
                                <input value={Array.isArray(editing.sizes) ? editing.sizes.join(", ") : editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" placeholder="S, M, L, XL" />
                            </Field>
                            <Field label="Occasions (comma-separated)">
                                <input value={Array.isArray(editing.occasion) ? editing.occasion.join(", ") : editing.occasion} onChange={(e) => setEditing({ ...editing, occasion: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" placeholder="Wedding, Festive" />
                            </Field>
                            <div className="lg:col-span-2">
                                <Field label="Images">
                                    <ProductImageEditor
                                        images={Array.isArray(editing.images) ? editing.images : (editing.images || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean)}
                                        onChange={(next) => setEditing({ ...editing, images: next })}
                                        testId="admin-product-image-editor"
                                    />
                                </Field>
                            </div>
                            <div className="lg:col-span-2">
                                <Field label="Description">
                                    <textarea rows={4} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                                </Field>
                            </div>
                            <div className="lg:col-span-2 flex flex-wrap gap-5">
                                <Toggle label="New arrival" value={editing.isNew} onChange={(v) => setEditing({ ...editing, isNew: v })} testId="admin-product-isnew-toggle" />
                                <Toggle label="Best seller" value={editing.isBestSeller} onChange={(v) => setEditing({ ...editing, isBestSeller: v })} testId="admin-product-bestseller-toggle" />
                                <Toggle label="In stock" value={editing.inStock} onChange={(v) => setEditing({ ...editing, inStock: v })} />
                                <Toggle label="Hidden" value={editing.isHidden} onChange={(v) => setEditing({ ...editing, isHidden: v })} testId="admin-product-hidden-toggle" />
                            </div>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={closeModal} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="admin-product-save-button">
                                {busy ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}

function Toggle({ label, value, onChange, testId }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={testId}>
            <span
                onClick={() => onChange(!value)}
                className={`w-10 h-6 rounded-full relative transition-colors ${value ? "bg-skyblueDeep" : "bg-inkDark/15"}`}
            >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-4" : ""}`} />
            </span>
            <span className="text-sm text-inkDark">{label}</span>
        </label>
    );
}
